'use client';

import Link from 'next/link';
import { FileText, Users, Settings, ArrowUpRight, Clock, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useUser } from '@clerk/nextjs';
import { formatCurrency } from '@/lib/utils';
import { StatsCards } from './sales/components/StatsCards';
import { SalesStats } from './sales/components/types';

export default function DashboardPageClient() {
  const { user } = useUser();
  const userId = user?.id;

  // Fetch sales statistics (same as sales page)
  const statsData = useQuery(
    api.sales.getUserSalesStats,
    userId ? { userId, timeFilter: 'all' } : 'skip',
  );

  const leadLists = useQuery(
    api.events.getSellerListings,
    userId
      ? {
          userId,
          status: 'active',
          searchTerm: '',
        }
      : 'skip',
  );

  const myOffers = useQuery(
    api.scrapers.getSellerScrapers,
    userId
      ? {
          userId,
          status: 'active',
          searchTerm: '',
        }
      : 'skip',
  );

  const myGroupBuys = useQuery(
    api.groupBuys.getMyGroupBuys,
    userId
      ? {
          userId,
          searchTerm: '',
          status: '',
          paginationOpts: { numItems: 10 },
        }
      : 'skip',
  );

  const recentActivity = useQuery(api.events.getRecentActivity, userId ? { userId } : 'skip') || [];
  const recentPurchases =
    useQuery(api.purchases.getUserRecentPurchases, userId ? { userId } : 'skip') || [];

  // Helper function to generate purchase links
  const getPurchaseLink = (purchase: any) => {
    switch (purchase.type) {
      case 'list':
        // Use slug if available, otherwise fall back to id
        return `/lists/${purchase.slug || purchase.id}`;
      case 'scraper':
        return `/scrapers/${purchase.id}`;
      case 'group-buy':
        return `/group-buys/${purchase.id}`;
      default:
        return '#'; // Fallback link
    }
  };

  // Handle export data
  const handleExportData = () => {
    // Export logic would go here
    alert('Data export functionality will be implemented soon.');
  };

  // Use statsData for stats, provide defaults if still loading
  const stats: SalesStats = statsData || {
    totalRevenue: 0,
    netRevenue: 0,
    platformFee: 0,
    totalSales: 0,
    averageOrderValue: 0,
    availableForPayout: 0,
    pendingPayouts: 0,
    processedPayouts: 0,
    canRequestPayout: false,
    minimumPayoutAmount: 50,
    payoutHistory: [],
  };

  return (
    <div>
      <div className="space-y-6">
        {/* Sales Stats Cards - Same as Sales Page */}
        <StatsCards stats={stats} onExportData={handleExportData} />

        {/* Dashboard Content */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Recent Activity */}
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Your latest sales, orders, and messages</CardDescription>
            </CardHeader>
            <CardContent>
              {!recentActivity || recentActivity.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  No recent activity to display
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity, i) => (
                    <div
                      key={i}
                      className="flex items-start justify-between border-b pb-4 last:border-0 last:pb-0"
                    >
                      <div className="space-y-1">
                        <p className="font-medium">{activity.title}</p>
                        <p className="text-sm text-muted-foreground">{activity.description}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {activity.time}
                        </p>
                      </div>
                      {activity.amount && (
                        <span className="text-sm font-medium">{activity.amount}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Purchases */}
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>My Recent Purchases</CardTitle>
              <CardDescription>Lists, scrapers, and group buys you've purchased</CardDescription>
            </CardHeader>
            <CardContent>
              {!recentPurchases || recentPurchases.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  No recent purchases to display
                </div>
              ) : (
                <div className="space-y-4">
                  {recentPurchases.map((purchase, i) => {
                    // Choose an icon based on purchase type
                    let Icon = FileText;
                    if (purchase.type === 'scraper') Icon = Settings;
                    if (purchase.type === 'group-buy') Icon = Users;
                    if (purchase.type === 'list') Icon = List;
                    // fallback if ListIcon doesn't exist

                    return (
                      <Link key={i} href={getPurchaseLink(purchase)} passHref>
                        <div className="group bg-card border border-border rounded-lg p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer hover:bg-muted/70 mb-4">
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-muted">
                              <Icon className="h-5 w-5 text-primary" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-base truncate">{purchase.title}</p>
                              <span className="text-xs text-muted-foreground capitalize">
                                {purchase.type.replace('-', ' ')}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              {purchase.pricePaid !== undefined && purchase.pricePaid !== null && (
                                <span className="text-sm font-bold text-primary">
                                  {formatCurrency(purchase.pricePaid)}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">{purchase.date}</span>
                            </div>
                          </div>
                          <span
                            className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ml-4
                              ${
                                purchase.status === 'Downloaded' || purchase.status === 'Completed'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                  : purchase.status === 'In Progress' ||
                                      purchase.status === 'Pending' ||
                                      purchase.status === 'Pending Access'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                              }
                            `}
                          >
                            {purchase.status}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
              <div className="mt-4">
                <Button variant="outline" className="w-full" asChild>
                  <Link href="/dashboard/purchases">
                    View All Purchases
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
